import React from "react";
import { Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PANEL } from "../design-system/panel/panelTokens";

const panelMarkdownSx = {
  fontSize: "0.84rem",
  color: PANEL.text.body,
  lineHeight: 1.6,
  wordBreak: "break-word",
  "& > :first-of-type": { mt: 0 },
  "& > :last-child": { mb: 0 },
  "& p": { m: 0, mb: 1 },
  "& p:last-child": { mb: 0 },
  "& strong": { fontWeight: 700, color: PANEL.text.primary },
  "& em": { fontStyle: "italic", color: PANEL.text.meta },
  "& a": {
    color: PANEL.accent.text,
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  "& ul, & ol": {
    m: "0 0 0.75rem",
    pl: "1.25rem",
  },
  "& li": { mb: 0.35 },
  "& li::marker": { color: PANEL.accent.main },
  "& h1, & h2, & h3, & h4": {
    m: "0.85rem 0 0.45rem",
    fontWeight: 700,
    letterSpacing: "-0.02em",
    color: PANEL.text.primary,
    lineHeight: 1.3,
  },
  "& h1": { fontSize: "1.05rem" },
  "& h2": { fontSize: "0.98rem" },
  "& h3, & h4": { fontSize: "0.9rem" },
  "& hr": {
    border: 0,
    borderTop: `1px solid ${PANEL.border}`,
    my: 1.25,
  },
  "& blockquote": {
    m: "0 0 0.75rem",
    pl: 1.25,
    borderLeft: `3px solid ${PANEL.accent.main}`,
    color: PANEL.text.muted,
  },
  "& code": {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    fontSize: "0.78rem",
    px: 0.55,
    py: 0.15,
    borderRadius: "5px",
    bgcolor: PANEL.fill.muted,
    border: `1px solid ${PANEL.border}`,
    color: PANEL.accent.text,
  },
  "& pre": {
    m: "0 0 0.85rem",
    p: 1.1,
    borderRadius: "10px",
    overflowX: "auto",
    bgcolor: PANEL.fill.muted,
    border: `1px solid ${PANEL.border}`,
  },
  "& pre code": {
    p: 0,
    border: "none",
    bgcolor: "transparent",
    fontSize: "0.76rem",
    color: PANEL.text.body,
  },
  "& table": {
    width: "100%",
    borderCollapse: "collapse",
    display: "block",
    overflowX: "auto",
    mb: 1,
    fontSize: "0.78rem",
  },
  "& thead th": {
    textAlign: "left",
    fontWeight: 700,
    color: PANEL.text.primary,
    bgcolor: PANEL.accent.soft,
    borderBottom: `1px solid ${PANEL.accent.softBorder22}`,
  },
  "& th, & td": {
    px: 1,
    py: 0.65,
    borderBottom: `1px solid ${PANEL.border}`,
    verticalAlign: "top",
    whiteSpace: "nowrap",
  },
  "& tbody tr:nth-of-type(even) td": {
    bgcolor: PANEL.fill.muted,
  },
};

function getThemeMarkdownSx(colors) {
  return {
    fontSize: "0.84rem",
    color: colors.grey[100],
    lineHeight: 1.6,
    wordBreak: "break-word",
    "& > :first-of-type": { mt: 0 },
    "& > :last-child": { mb: 0 },
    "& p": { m: 0, mb: 1 },
    "& p:last-child": { mb: 0 },
    "& strong": { fontWeight: 700, color: colors.grey[100] },
    "& em": { fontStyle: "italic", color: colors.grey[200] },
    "& a": {
      color: colors.greenAccent[300],
      textDecoration: "underline",
      textUnderlineOffset: 2,
    },
    "& ul, & ol": {
      m: "0 0 0.75rem",
      pl: "1.25rem",
    },
    "& li": { mb: 0.35 },
    "& li::marker": { color: colors.greenAccent[400] },
    "& h1, & h2, & h3, & h4": {
      m: "0.85rem 0 0.45rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: colors.grey[100],
      lineHeight: 1.3,
    },
    "& h1": { fontSize: "1.05rem" },
    "& h2": { fontSize: "0.98rem" },
    "& h3, & h4": { fontSize: "0.9rem" },
    "& hr": {
      border: 0,
      borderTop: `1px solid ${colors.grey[700]}66`,
      my: 1.25,
    },
    "& blockquote": {
      m: "0 0 0.75rem",
      pl: 1.25,
      borderLeft: `3px solid ${colors.greenAccent[600]}`,
      color: colors.grey[300],
    },
    "& code": {
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
      fontSize: "0.78rem",
      px: 0.55,
      py: 0.15,
      borderRadius: "5px",
      bgcolor: `${colors.primary[900]}66`,
      border: `1px solid ${colors.grey[700]}55`,
      color: colors.greenAccent[200] || colors.greenAccent[300],
    },
    "& pre": {
      m: "0 0 0.85rem",
      p: 1.1,
      borderRadius: "10px",
      overflowX: "auto",
      bgcolor: `${colors.primary[900]}88`,
      border: `1px solid ${colors.grey[700]}55`,
    },
    "& pre code": {
      p: 0,
      border: "none",
      bgcolor: "transparent",
      fontSize: "0.76rem",
      color: colors.grey[100],
    },
    "& table": {
      width: "100%",
      borderCollapse: "collapse",
      display: "block",
      overflowX: "auto",
      mb: 1,
      fontSize: "0.78rem",
    },
    "& thead th": {
      textAlign: "left",
      fontWeight: 700,
      color: colors.grey[100],
      bgcolor: `${colors.greenAccent[800]}40`,
      borderBottom: `1px solid ${colors.greenAccent[600]}55`,
    },
    "& th, & td": {
      px: 1,
      py: 0.65,
      borderBottom: `1px solid ${colors.grey[700]}55`,
      verticalAlign: "top",
      whiteSpace: "nowrap",
    },
    "& tbody tr:nth-of-type(even) td": {
      bgcolor: `${colors.primary[800]}40`,
    },
  };
}

/**
 * Chat-friendly GFM markdown (bold, lists, tables, code) styled for PatientMesh theme.
 */
export default function ChatMarkdown({ content, colors, variant = "theme", safeContext = false }) {
  if (content == null || content === "") return null;

  const sx = variant === "panel" ? panelMarkdownSx : getThemeMarkdownSx(colors);

  return (
    <Box sx={sx}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={safeContext ? { img: () => null, a: ({ children }) => <span>{children}</span> } : undefined}>{String(content)}</ReactMarkdown>
    </Box>
  );
}
